// frontend-react/src/components/sales/variants.js
// Helpers pour la gestion des variantes de produits
import { toMoney } from '../../utils/helpers';

function normalizeStr(v) {
  return String(v ?? '').trim();
}

function normalizeCode(v) {
  return normalizeStr(v).toUpperCase().replace(/\s+/g, '');
}

export function groupVariantsByType(list) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach(v => {
    const t = v?.variant_type;
    if (!t) return;
    if (!map[t]) map[t] = [];
    map[t].push(v);
  });
  Object.keys(map).forEach(t => {
    map[t] = map[t]
      .slice()
      .sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0));
  });
  return map;
}

export function variantTypeLabel(t, rulesByType) {
  const ruleLabel = rulesByType?.[t]?.ui_label;
  if (ruleLabel) return ruleLabel;
  const x = String(t || '').toLowerCase();
  if (x === 'accessoire') return 'Accessoire';
  if (x === 'nb_bougies') return 'Nombre de bougies';
  if (x === 'type_bougie') return 'Type de bougie';
  if (x === 'type_sceau') return 'Type de sceau';
  return t;
}

export function parseNumericFromCode(code) {
  const s = String(code || '').trim();
  const m = s.match(/(\d+)/);
  if (!m) return 1;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function parseNbFromCode(code) {
  return parseNumericFromCode(code);
}

export function findVariant(byType, variant_type, code) {
  const list = byType?.[variant_type] || [];
  const wanted = normalizeCode(code);
  return list.find(v => normalizeCode(v?.code) === wanted) || null;
}

export function getPriceDelta(byType, variant_type, code) {
  const v = findVariant(byType, variant_type, code);
  const d = Number(v?.price_delta ?? 0);
  return Number.isFinite(d) ? d : 0;
}

function legacyRuleForType(variant_type) {
  if (variant_type === 'type_bougie') {
    return { price_mode: 'add_times_variant', price_multiplier_type: 'nb_bougies' };
  }
  return { price_mode: 'none', price_multiplier_type: null };
}

function getRuleForType(variant_type, rulesByType, rulesConfigured) {
  if (rulesByType && rulesByType[variant_type]) return rulesByType[variant_type];
  if (!rulesConfigured) return legacyRuleForType(variant_type);
  return { price_mode: 'add', price_multiplier_type: null };
}

// toMoney importé de utils/helpers.js

/**
 * computeUnitPriceForLine
 * - Estimation front alignée avec backend (règles par type)
 */
export function computeUnitPriceForLine({ productUnitPrice, selectedVariants, byType, rulesByType, rulesConfigured }) {
  let unit = Number(productUnitPrice || 0);
  if (!Number.isFinite(unit)) unit = 0;

  const selected = selectedVariants || {};
  for (const [variant_type, code] of Object.entries(selected)) {
    if (!code) continue;
    const v = findVariant(byType, variant_type, code);
    if (!v) continue;

    const rule = getRuleForType(variant_type, rulesByType, rulesConfigured);
    if (!rule || rule.price_mode === 'none') continue;

    const base = Number(v?.price_delta ?? 0);
    if (!Number.isFinite(base) || base === 0) continue;

    if (rule.price_mode === 'add') {
      unit += base;
      continue;
    }

    if (rule.price_mode === 'add_times_variant') {
      const multCode = selected?.[rule.price_multiplier_type];
      const mult = parseNumericFromCode(multCode);
      unit += base * mult;
    }
  }

  return toMoney(unit);
}

/**
 * buildVariantsFromComposition
 * - Prend ta composition UI (ligne) et un catalogue variantsByType (groupé)
 * - Retourne [{variant_type, code}] pour chaque type présent
 */
export function buildVariantsFromComposition(composition, variantsByType) {
  const comp = composition || {};
  const out = [];

  const types = Object.keys(variantsByType || {});
  if (types.length === 0) return out;

  function pickCode(variant_type, wanted) {
    const list = variantsByType?.[variant_type] || [];
    if (!list.length) return null;

    const w = normalizeCode(wanted);

    // 1) Match direct sur code
    const direct = list.find(v => normalizeCode(v.code) === w);
    if (direct) return direct.code;

    // 2) Match sur label
    const byLabel = list.find(v => normalizeCode(v.label) === w);
    if (byLabel) return byLabel.code;

    return null;
  }

  for (const t of types) {
    const code = pickCode(t, comp[t]);
    if (code) out.push({ variant_type: t, code });
  }

  return out;
}

export function getDefaultSelectedVariants(byType) {
  const selected = {};
  Object.keys(byType || {}).forEach(t => {
    const first = (byType[t] || [])[0];
    if (first?.code) selected[t] = first.code;
  });
  return selected;
}
